/**
 * Recovery for stale code-split chunks after a deploy.
 *
 * Shell: any (entry-level). The app code-splits every route with
 * `React.lazy(() => import(...))`, so the browser holds references to
 * content-hashed chunk filenames (e.g. `SignInPage-Ch5AMY8a.js`). When a new
 * build ships to Cloudflare Pages the old hashes are deleted; a tab loaded
 * against the previous build then fails to import the now-missing chunk — the
 * SPA fallback serves `index.html` (MIME `text/html`) where a JS module was
 * expected, the dynamic import rejects, and React Router surfaces a crash. This
 * is what users hit after leaving a tab open across a deploy (often correlated
 * with "idle", because session expiry forces a navigation that loads a chunk).
 *
 * The fix is to reload once on such a failure: `index.html` is served
 * `no-cache` (see apps/web/public/_headers), so the reload pulls fresh markup
 * referencing the new hashes and recovers transparently.
 *
 * The reload is time-guarded (one per {@link MIN_RELOAD_INTERVAL_MS}) so a
 * genuinely broken build — where the chunk is missing even on the fresh
 * deploy — cannot trap the user in a reload loop.
 */

/** Reload at most once per this window, to break reload loops on a truly broken build. */
const MIN_RELOAD_INTERVAL_MS = 10_000;
const RELOAD_AT_KEY = 'storefront:chunkReloadAt';

const CHUNK_ERROR_PATTERN =
  /(error loading dynamically imported module|failed to fetch dynamically imported module|importing a module script failed|disallowed mime type|loading chunk [\w-]+ failed|loading css chunk)/i;

/**
 * True when `error` looks like a failed dynamic-import / chunk load — the
 * symptom of a stale content-hashed bundle after a deploy. Matches the messages
 * the major engines emit (Chrome "Failed to fetch dynamically imported module",
 * Firefox "error loading dynamically imported module", Safari "Importing a
 * module script failed", plus the MIME-type block).
 */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : typeof (error as { message?: unknown } | null)?.message === 'string'
          ? (error as { message: string }).message
          : null;
  return message !== null && CHUNK_ERROR_PATTERN.test(message);
}

/**
 * Force a one-time, time-guarded full reload to pick up the latest build.
 * Returns true if a reload was triggered, false if one happened too recently
 * (loop guard). Call this only when a chunk failure is already established
 * (e.g. from the `vite:preloadError` event); use {@link maybeReloadForChunkError}
 * when the error needs classifying first.
 */
export function reloadForFreshChunks(): boolean {
  if (!withinReloadGuard()) return false;
  window.location.reload();
  return true;
}

/**
 * If `error` is a chunk-load failure, force a one-time reload and return true.
 * Returns false when the error is unrelated, or when a reload happened within
 * {@link MIN_RELOAD_INTERVAL_MS}. Safe to call from React error boundaries.
 */
export function maybeReloadForChunkError(error: unknown): boolean {
  return isChunkLoadError(error) && reloadForFreshChunks();
}

/** Records the reload time; returns false if a reload happened too recently. */
function withinReloadGuard(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_AT_KEY) ?? '0');
    if (Number.isFinite(last) && Date.now() - last < MIN_RELOAD_INTERVAL_MS) return false;
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // sessionStorage blocked (private mode / disabled) — fall through and reload anyway.
  }
  return true;
}
